import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Radio, Send, MailCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SentConfirmation = ({ email }) => (
  <div data-testid="forgot-success">
    <MailCheck className="w-8 h-8 text-[#007AFF] mb-4" />
    <h2 className="font-heading text-3xl font-black tracking-tighter mb-3">Check your email</h2>
    <p className="text-sm text-muted-foreground mb-6">
      If <span className="text-white font-mono">{email}</span> has an account, a reset link is on its way.
      The link expires in 1 hour — check spam if you don't see it.
    </p>
    <Link to="/login" data-testid="back-to-login"
      className="text-[#007AFF] hover:underline text-sm flex items-center gap-1.5">
      <ArrowLeft className="w-4 h-4" /> Back to sign in
    </Link>
  </div>
);

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-8">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 bg-[#007AFF] flex items-center justify-center">
            <Radio className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading font-black text-xl tracking-tighter">Incident Tracking &amp; Inquiry Services</span>
        </div>

        {sent ? (
          <SentConfirmation email={email} />
        ) : (
          <form onSubmit={submit} data-testid="forgot-form">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Account Recovery</p>
            <h2 className="font-heading text-3xl font-black tracking-tighter mb-3">Reset password</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Enter your account email and we'll send you a link to choose a new password.
            </p>

            {error && (
              <div className="mb-4 border border-[#FF3B30]/40 bg-[#FF3B30]/10 text-[#FF3B30] text-sm px-3 py-2" data-testid="forgot-error">
                {error}
              </div>
            )}

            <Label className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Email</Label>
            <Input data-testid="forgot-email" type="email" required value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 bg-[#0A0A0A] border-border rounded-sm focus-visible:ring-[#007AFF]" placeholder="you@company.com" />

            <Button type="submit" disabled={loading} data-testid="forgot-submit"
              className="w-full mt-6 bg-[#007AFF] hover:bg-[#0066d6] text-white rounded-sm gap-2 font-semibold h-11">
              {loading ? "Sending…" : "Send reset link"} <Send className="w-4 h-4" />
            </Button>

            <p className="text-sm text-muted-foreground mt-6">
              Remembered it?{" "}
              <Link to="/login" data-testid="forgot-back-login" className="text-[#007AFF] hover:underline">Sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
